import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { IAccount, IAccountTag } from "./InvoiceTypes";
import { ErpType } from "src/network/User";
import { GET_ACCOUNT_DATA, GET_ACCOUNT_TAG_DATA } from "src/api/cred";
import { GetAccountData, GetAccountDataVariables } from "src/api/generated/GetAccountData";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GetAccountTagData, GetAccountTagDataVariables } from "src/api/generated/GetAccountTagData";
import { INVOICE_ACCOUNT_UNIT_OF_MEASURE, INVOICE_ACCOUNT_VAT_CONFIG } from "./InvoiceEnums";

export enum LOADING_ACCOUNT_DATA_STATE {
    INIT,
    LOADING
}

export const MAX_ACCOUNT_TAGS_TO_SHOW = 2;

export class AccountingAccountSearchStore {
    rootStore: RootStore;

    erpType: ErpType = ErpType.IT2;

    accountData: IAccount[] = [];
    accountTagData: Array<IAccountTag[]> = [];
    displayAllAccountTags: boolean[] = [];
    loadingAccountDataState: LOADING_ACCOUNT_DATA_STATE = LOADING_ACCOUNT_DATA_STATE.INIT;
    loadedAccountDataWithVariables: { realestateId: string; bookingDate: string } | undefined = undefined;
    // Request cache for GET_ACCOUNT_TAG_DATA keyed by its query variables (creditor, realestate,
    // booking date). Rows sharing the same key share one request, including while it is in flight.
    // Not observable on purpose - it is plumbing, not UI state.
    private accountTagRequests: Map<string, Promise<IAccountTag[]>> = new Map();

    // This is an array because every Accounting Row has its own account value
    currentAccountSearchQuery: string[] = [];
    selectedAccountQueryString: string[] = [];
    selectedAccountName: string[] = [];

    triggerFocusNextInput: { isActive: boolean; index: number } = { isActive: false, index: 0 };
    triggerFocusAccountInput: { isActive: boolean; index: number } = { isActive: false, index: 0 };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            accountData: observable,
            accountTagData: observable,
            currentAccountSearchQuery: observable,
            selectedAccountQueryString: observable,
            selectedAccountName: observable,
            triggerFocusNextInput: observable,
            loadingAccountDataState: observable,
            loadedAccountDataWithVariables: observable,
            displayAllAccountTags: observable,
            triggerFocusAccountInput: observable,

            init: action,
            reset: action,
            removeAccountingRow: action,
            loadAccountData: action,
            loadAccountTagData: action,
            setLoadingAccountDataState: action,
            setCurrentAccountSearchQuery: action,
            setSelectedAccountQueryString: action,
            setSelectedAccountName: action,
            setTriggerFocusNextInput: action,
            filteredAccountData: action,
            setAccountData: action,
            setAccountTagData: action,
            getAllAccountTags: action,
            getAccountTagsCount: action,
            getAccountById: action,
            setDisplayAllAccountTags: action,
            setTriggerFocusAccountInput: action,

            displayedAccountTagsByIndex: computed,
            areAccountTagsDisplayedByIndex: computed
        });
    }

    init = () => {};

    reset = () => {
        this.currentAccountSearchQuery = [];
        this.selectedAccountQueryString = [];
        this.selectedAccountName = [];
        this.accountTagData = [];
        this.displayAllAccountTags = [];
        this.accountTagRequests.clear();
    };

    removeAccountingRow = (accountingRowIndex: number) => {
        this.currentAccountSearchQuery.splice(accountingRowIndex, 1);
        this.selectedAccountQueryString.splice(accountingRowIndex, 1);
        this.selectedAccountName.splice(accountingRowIndex, 1);
        this.accountTagData.splice(accountingRowIndex, 1);
        this.displayAllAccountTags.splice(accountingRowIndex, 1);
    };

    loadAccountData = async (accountingRowIndex: number) => {
        this.setLoadingAccountDataState(LOADING_ACCOUNT_DATA_STATE.LOADING);

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;
        const realestateId = currentInvoice?.getAccountingRealestate(accountingRowIndex)?.id;
        const date = currentInvoice?.getAccountingDate(accountingRowIndex);

        const hasAlreadyLoadedAccountData =
            this.loadedAccountDataWithVariables?.realestateId === realestateId &&
            this.loadedAccountDataWithVariables?.bookingDate === date;

        if (realestateId && date && !hasAlreadyLoadedAccountData) {
            this.loadedAccountDataWithVariables = {
                realestateId: realestateId,
                bookingDate: date
            };

            const { data }: { data: GetAccountData } = await apolloClientInstance.query<
                GetAccountData,
                GetAccountDataVariables
            >({
                query: GET_ACCOUNT_DATA,
                variables: {
                    realestateid: realestateId,
                    bookingdate: date
                }
            });

            const accountData: IAccount[] = data.accounts
                .map((account) => {
                    return {
                        id: account.id,
                        number: account.number,
                        name: account.name,
                        requiresCostCenter: account.requirescostcenter,
                        requiresQuantity: account.requiresquantitiy,
                        unitOfMeasure: account.unitofmeasure ?? INVOICE_ACCOUNT_UNIT_OF_MEASURE.ZERO,
                        extraCostDate: account.extracostdate,
                        vatConfig: account.vatconfig ?? INVOICE_ACCOUNT_VAT_CONFIG.WITHOUT_VAT,
                        defaultVatCodeId: account.defaultvatcodeid ?? undefined,
                        validTo: account.validto,
                        validFrom: account.validfrom,
                        realestateId: account.realestate?.id ?? undefined
                    };
                })
                .sort((a, b) => {
                    // The result set comes already sorted by 'type'
                    // Then also sort by "number" (lexicographic or numeric comparison)
                    const numberComparison = a.number.localeCompare(b.number, undefined, {
                        numeric: true,
                        sensitivity: "base"
                    });
                    if (numberComparison !== 0) {
                        return numberComparison;
                    }
                    // Finally by "name" (string comparison)
                    return a.name.localeCompare(b.name);
                });

            this.setAccountData(accountData);
        }

        this.setLoadingAccountDataState(LOADING_ACCOUNT_DATA_STATE.INIT);
    };

    loadAccountTagData = async (
        creditorId: string,
        realestateId: string,
        bookingDate: string,
        accountingRowIndex: number
    ) => {
        const accountTagData = await this.fetchAccountTagData(creditorId, realestateId, bookingDate);

        this.setAccountTagData(accountTagData, accountingRowIndex);
    };

    private fetchAccountTagData = (
        creditorId: string,
        realestateId: string,
        bookingDate: string
    ): Promise<IAccountTag[]> => {
        const cacheKey = `${creditorId}|${realestateId}|${bookingDate}`;

        const pendingRequest = this.accountTagRequests.get(cacheKey);
        if (pendingRequest) {
            return pendingRequest;
        }

        const request = apolloClientInstance
            .query<GetAccountTagData, GetAccountTagDataVariables>({
                query: GET_ACCOUNT_TAG_DATA,
                variables: {
                    creditorId: creditorId,
                    realestateId: realestateId,
                    bookingDate: bookingDate
                }
            })
            .then(({ data }) => {
                const seenAccountIds = new Set<string>();

                return data.accounts_history
                    .filter((accountHistoryEntry) => {
                        if (seenAccountIds.has(accountHistoryEntry.account_id)) {
                            return false; // Skip duplicates
                        }
                        seenAccountIds.add(accountHistoryEntry.account_id);
                        return true;
                    })
                    .map((accountHistoryEntry) => {
                        return {
                            accountId: accountHistoryEntry.account_id,
                            number: accountHistoryEntry.account_number ?? undefined,
                            name: accountHistoryEntry.account_name ?? undefined,
                            accountingsHistoryText: accountHistoryEntry.accountings_history_text ?? undefined,
                            numberOfRecords: accountHistoryEntry.number_of_records ?? undefined
                        };
                    });
            })
            .catch((error) => {
                // Do not cache failures - the next caller gets a fresh attempt.
                this.accountTagRequests.delete(cacheKey);
                throw error;
            });

        this.accountTagRequests.set(cacheKey, request);

        return request;
    };

    // Getters
    getFormattedAccountFieldValue = (account: IAccount): string => {
        return `${account.number} ${account.name}`;
    };

    getCurrentAccountSearchQuery(accountingRowIndex: number) {
        return this.currentAccountSearchQuery[accountingRowIndex];
    }

    getSelectedAccountQueryString(accountingRowIndex: number) {
        return this.selectedAccountQueryString[accountingRowIndex];
    }

    getSelectedAccountName(accountingRowIndex: number) {
        return this.selectedAccountName[accountingRowIndex];
    }

    getAccountById = async (accountId: string, accountingRowIndex: number): Promise<IAccount | undefined> => {
        if (this.accountData.length === 0) {
            await this.loadAccountData(accountingRowIndex);
        }
        return this.accountData.find((account) => account.id === accountId);
    };

    // Setters
    setLoadingAccountDataState(loadingAccountDataState: LOADING_ACCOUNT_DATA_STATE) {
        this.loadingAccountDataState = loadingAccountDataState;
    }

    setAccountData(data: IAccount[]) {
        this.accountData = data;
    }

    setAccountTagData(accountTagData: IAccountTag[], accountingRowIndex: number) {
        this.accountTagData[accountingRowIndex] = accountTagData;
    }

    setCurrentAccountSearchQuery(currentAccountSearchQuery: string, accountingRowIndex: number) {
        this.currentAccountSearchQuery[accountingRowIndex] = currentAccountSearchQuery;
    }

    setSelectedAccountQueryString(selectedAccountQueryString: string, accountingRowIndex: number) {
        this.selectedAccountQueryString[accountingRowIndex] = selectedAccountQueryString;
    }

    setSelectedAccountName(selectedAccountName: string, accountingRowIndex: number) {
        this.selectedAccountName[accountingRowIndex] = selectedAccountName;
    }

    setDisplayAllAccountTags(displayAllAccountTags: boolean, accountingRowIndex: number) {
        this.displayAllAccountTags[accountingRowIndex] = displayAllAccountTags;
    }

    /*  This function triggers the focus on the next field after selecting an account
        For IT2 this will be the CostCenter field in the current accounting row
        For RIMO this will be the Booking Text field in the current accounting row
    */
    setTriggerFocusNextInput(triggerFocusNextInput: boolean, accountingRowIndex: number) {
        this.triggerFocusNextInput = {
            isActive: triggerFocusNextInput,
            index: accountingRowIndex
        };
    }

    setTriggerFocusAccountInput(triggerFocusAccountInput: boolean, accountingRowIndex: number) {
        this.triggerFocusAccountInput = {
            isActive: triggerFocusAccountInput,
            index: accountingRowIndex
        };
    }

    filteredAccountData(accountingRowIndex: number): IAccount[] {
        let currentAccountSearchQuery = this.currentAccountSearchQuery[accountingRowIndex] ?? "";
        let selectedRealestateQueryString = this.selectedAccountQueryString[accountingRowIndex] ?? "";

        if (currentAccountSearchQuery === selectedRealestateQueryString) {
            return this.accountData;
        }

        if (currentAccountSearchQuery === "") {
            return this.accountData;
        }

        const filteredAccountData = this.accountData.filter((account) => {
            const accountName = `${account.number} ${account.name}`;

            return accountName.toLowerCase().includes(currentAccountSearchQuery.toLowerCase());
        });

        return filteredAccountData;
    }

    get areAccountTagsDisplayedByIndex(): boolean[] {
        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;

        return this.accountTagData.map((_, index) => {
            return (
                !!currentInvoice && // Ensure currentInvoice is truthy (returns false if it's null or undefined)
                !currentInvoice.isAccountingAccountSelected(index) &&
                this.displayedAccountTagsByIndex[index] &&
                this.displayedAccountTagsByIndex[index].length > 0
            );
        });
    }

    getAllAccountTags = (accountingRowIndex: number): IAccountTag[] => {
        if (this.accountTagData[accountingRowIndex]) {
            return this.accountTagData[accountingRowIndex];
        } else {
            return [];
        }
    };

    hasMoreAccountTagsToDisplay = (accountingRowIndex: number): boolean => {
        return (
            this.getAccountTagsCount(accountingRowIndex) > MAX_ACCOUNT_TAGS_TO_SHOW &&
            (this.displayAllAccountTags[accountingRowIndex] === undefined ||
                this.displayAllAccountTags[accountingRowIndex] === false)
        );
    };

    getAccountTagsCount = (accountingRowIndex: number): number => {
        if (this.accountTagData[accountingRowIndex]) {
            return this.accountTagData[accountingRowIndex].length;
        } else {
            return 0;
        }
    };

    get displayedAccountTagsByIndex(): IAccountTag[][] {
        return this.accountTagData.map((accountTags, index) => {
            const allAccountTagsAreDisplayed = this.displayAllAccountTags[index] === true;
            if (allAccountTagsAreDisplayed || this.getAccountTagsCount(index) <= MAX_ACCOUNT_TAGS_TO_SHOW) {
                return accountTags;
            } else if (this.hasMoreAccountTagsToDisplay(index)) {
                return accountTags.slice(0, MAX_ACCOUNT_TAGS_TO_SHOW);
            }
            return [];
        });
    }
}
